import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { ICE_COLORS } from '../CyanMaterial';

const IceBlockPlaceEffect = ({ position, color, onComplete }) => {
  const groupRef = useRef();
  const startTime = useRef(Date.now());
  const crystalsRef = useRef();
  const mistRef = useRef();

  const crystalCount = 10;
  const crystalData = useMemo(() => {
    const data = [];
    for (let i = 0; i < crystalCount; i++) {
      const angle = (i / crystalCount) * Math.PI * 2;
      data.push({
        angle,
        speed: 1.5 + Math.random() * 1.5,
        size: 0.06 + Math.random() * 0.04,
        rotSpeed: (Math.random() - 0.5) * 10,
      });
    }
    return data;
  }, []);

  const mistCount = 6;
  const mistData = useMemo(() => {
    const data = [];
    for (let i = 0; i < mistCount; i++) {
      const angle = (i / mistCount) * Math.PI * 2 + Math.PI / mistCount;
      data.push({
        angle,
        speed: 0.8 + Math.random() * 0.6,
      });
    }
    return data;
  }, []);

  useFrame(() => {
    if (!groupRef.current) return;

    const elapsed = (Date.now() - startTime.current) / 1000;
    const duration = 0.4;

    if (elapsed > duration) {
      onComplete?.();
      return;
    }

    const progress = elapsed / duration;

    // Expand crystals outward
    if (crystalsRef.current) {
      crystalsRef.current.children.forEach((crystal, i) => {
        const data = crystalData[i];
        const distance = data.speed * progress;
        crystal.position.x = Math.cos(data.angle) * distance;
        crystal.position.y = Math.sin(data.angle) * distance + progress * 0.3;
        crystal.rotation.z += data.rotSpeed * 0.02;
        crystal.scale.setScalar(data.size * (1 - progress * 0.7));
        crystal.material.opacity = (1 - progress) * 0.8;
      });
    }

    // Expand mist clouds
    if (mistRef.current) {
      mistRef.current.children.forEach((mist, i) => {
        const data = mistData[i];
        const distance = data.speed * progress;
        mist.position.x = Math.cos(data.angle) * distance;
        mist.position.y = Math.sin(data.angle) * distance;
        mist.scale.setScalar(0.8 + progress * 0.5);
        mist.material.opacity = (1 - progress) * 0.4;
      });
    }

    // Central frost flash
    if (groupRef.current.children[0]) {
      groupRef.current.children[0].material.opacity = (1 - progress) * 0.7;
      groupRef.current.children[0].scale.setScalar(1 + progress * 0.6);
    }
  });

  const frostColor = new THREE.Color(ICE_COLORS.frost);
  const crystalColor = new THREE.Color(ICE_COLORS.crystal);

  return (
    <group ref={groupRef} position={position}>
      {/* Central frost flash */}
      <mesh>
        <planeGeometry args={[0.9, 0.9]} />
        <meshBasicMaterial
          color={frostColor}
          transparent
          opacity={0.7}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      {/* Ice crystals */}
      <group ref={crystalsRef}>
        {crystalData.map((data, i) => (
          <mesh key={i}>
            <tetrahedronGeometry args={[data.size]} />
            <meshBasicMaterial
              color={crystalColor}
              transparent
              opacity={0.8}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>
        ))}
      </group>

      {/* Frost mist clouds */}
      <group ref={mistRef}>
        {mistData.map((data, i) => (
          <mesh key={i}>
            <circleGeometry args={[0.12, 8]} />
            <meshBasicMaterial
              color={frostColor}
              transparent
              opacity={0.4}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>
        ))}
      </group>
    </group>
  );
};

export default IceBlockPlaceEffect;
